import { nextTick } from "vue";
import { client_domain } from "@/main.js";
import Swal from "sweetalert2";

const PENDING_RSVP_SESSION_KEYS = [
  "next",
  "selectedEvent",
  "selectedExtras",
  "selectedExtraItems",
  "selectedItemId",
  "businessName",
  "isFirstTime",
  "chapter",
];

function get_pending_rsvp_session_value(key, fallback_value) {
  return JSON.parse(sessionStorage.getItem(key)) || fallback_value;
}

function clear_pending_rsvp_session() {
  PENDING_RSVP_SESSION_KEYS.forEach((key) => {
    sessionStorage.removeItem(key);
  });
}

export const view_events_lifecycle_methods = {
  start_event_badge_timer() {
    this.event_badge_timer_id = window.setInterval(() => {
      this.event_badge_now_ms = Date.now();
    }, 60000);
  },
  register_scroll_container() {
    nextTick(() => {
      if (this.$refs.scrollContainer) {
        this.$refs.scrollContainer.addEventListener("scroll", this.handleScroll);
      }
    });
  },
  register_event_tabs() {
    this.$nextTick(() => {
      $("#past-tab").one("shown.bs.tab", () => {
        this.filter_past_events();
      });

      [
        ["#upcoming-tab", "upcoming"],
        ["#my_events-tab", "my_events"],
        ["#past-tab", "past"],
        ["#my_tickets_tab", "my_tickets"],
        ["#all_tickets_tab", "all_tickets"],
      ].forEach(([selector, tabId]) => {
        const el = $(selector);
        if (!el.length) return;
        el.on("shown.bs.tab", () => this.set_active_tab(tabId));
      });

      this.set_active_tab("upcoming");
    });
  },
  restore_pending_rsvp() {
    if (!this.should_restore_pending_rsvp()) return;
    this.apply_pending_rsvp_session();
    this.submit_pending_rsvp();
  },
  should_restore_pending_rsvp() {
    return Boolean(sessionStorage.getItem("next") && this.token_exists);
  },
  apply_pending_rsvp_session() {
    this.selectedEvent = get_pending_rsvp_session_value("selectedEvent", null);
    this.selectedExtraId = get_pending_rsvp_session_value("selectedExtras", []);
    this.selectedExtraItemId = get_pending_rsvp_session_value("selectedExtraItems", []);
    this.selectedItemId = get_pending_rsvp_session_value("selectedItemId", []);
    this.businessName = get_pending_rsvp_session_value("businessName", "");
    this.isFirstTime = get_pending_rsvp_session_value("isFirstTime", false);
    this.chapter = get_pending_rsvp_session_value("chapter", "");
  },
  submit_pending_rsvp() {
    if (typeof this.addRsvp !== "function") return;
    this.addRsvp();
    clear_pending_rsvp_session();
  },
  reset_event_map_autocomplete() {
    this.map_auto_complete = null;
    this.map_auto_complete_input = null;
  },
  normalize_url(url) {
      const value = String(url || "").trim();
      if (!value) return "";
      if (/^https?:\/\//i.test(value)) return value;
      return `https://${value}`;
    },
  register_event_modals() {
    const event_modal = $("#event_modal");
    event_modal.on("show.bs.modal", () => {
      this.fetchEventTypes();
      this.fetchPortals();
      this.fetchRecurringTypes();
    });
    event_modal.on("shown.bs.modal", () => {
      this.load_google_map_event();
    });

    const eventModal = document.getElementById("event_modal");
    eventModal?.addEventListener("hidden.bs.modal", () => {
      this.clearForm();
      this.event_id = null;
      this.reset_event_map_autocomplete();
    });

    $("#edit_event_modal").on("shown.bs.modal", () => {
      this.load_google_map_event();
    });

    const editEventModal = document.getElementById("edit_event_modal");
    editEventModal?.addEventListener("hidden.bs.modal", () => {
      this.reset_event_map_autocomplete();
    });
  },
  load_styled_calendar_script() {
    const script = document.createElement("script");
    script.src = "https://embed.styledcalendar.com/assets/parent-window.js";
    script.async = true;
    script.type = "module";
    document.body.appendChild(script);
  },
  register_calendar_modal_render() {
    this.$nextTick(() => {
      const modalEl = this.$refs.calendarModal;
      if (!modalEl) return;

      $(modalEl).on("shown.bs.modal", () => {
        if (this.$refs.fullCalendar) {
          this.$refs.fullCalendar.getApi().render();
        }
      });
    });
  },
  set_client_domain_flags() {
    this.cbo_domain = client_domain.includes("cbo");
    this.workbook_domain = client_domain.includes("workbook");
  },
  initialize_rsvp_table() {
    this.$nextTick(() => {
      $("#rsvpTable").DataTable();
    });
  },
  register_view_events_listeners() {
    this._view_events_document_click_handler = () => {
      this.share_menu_open = {};
    };
    document.addEventListener("click", this._view_events_document_click_handler);

    this._realtime_event_handler = (evt) => this.handle_realtime_event(evt);
    window.addEventListener("realtime:event", this._realtime_event_handler);
  },
};

function get_date_part(date_value, index) {
  return Reflect.get(String(date_value ?? "").split("T"), index) || "";
}

function get_updated_events(events = [], updated_event = {}) {
  return events.map((event) => (event.id === updated_event.id ? updated_event : event));
}

export const view_events_update_methods = {
  show_update_event_loading() {
    Swal.fire({
      title: "Updating...",
      text: "Please wait while we update the event.",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
  },
  append_update_event_description(formData) {
    this.event_description = this.$refs.quillEventEditorRef.getHTML();
    formData.append("description", this.event_description);
  },
  append_update_event_dates(formData) {
    formData.append("start_date", get_date_part(this.start_date, 0));
    formData.append("start_time", get_date_part(this.start_date, 1));
    formData.append("end_date", get_date_part(this.end_date, 0));
    formData.append("end_time", get_date_part(this.end_date, 1));
  },
  append_update_event_link(formData) {
    if (!this.event_link) return;
    formData.append("event_link", this.event_link);
  },
  append_update_event_capacity(formData) {
    if (this.event_capacity <= 0) return;
    formData.append("rsvp_max_capacity", this.event_capacity);
  },
  build_update_event_form_data() {
    const formData = new FormData();
    formData.append("event_id", this.event_id);
    formData.append("event_name", this.event_name);
    this.append_update_event_description(formData);
    this.append_update_event_dates(formData);
    formData.append("location", this.location);
    formData.append("event_type", this.event_type.id);
    formData.append("is_free", this.is_free ? "true" : "false");
    this.append_update_event_link(formData);
    this.append_update_event_capacity(formData);
    return formData;
  },
  replace_updated_event(updated_event) {
    this.upcoming_events = get_updated_events(this.upcoming_events, updated_event);
  },
  close_edit_event_modal_and_reload() {
    $("#edit_event_modal").modal("hide");
    this.load_event();
  },
  show_update_event_success(message) {
    Swal.fire({
      icon: "success",
      title: "Success",
      text: message,
      showConfirmButton: false,
      timer: 2000,
      position: "bottom-end",
      toast: true,
    }).then(() => {
      this.close_edit_event_modal_and_reload();
    });
  },
  show_update_event_error() {
    Swal.close();
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "An error occurred while trying to update the Event, please try again.",
      showConfirmButton: false,
      timer: 2000,
      position: "bottom-end",
      toast: true,
    });
  },
  handle_update_event_response(response) {
    if (response.status !== "success") return;
    this.replace_updated_event(response.event);
    this.show_update_event_success(response.message);
  },
  submit_update_event_form(formData) {
    $.ajax({
      type: "POST",
      url: `${process.env.VUE_APP_BACKEND_API_URL}/event_management/update_event_api/`,
      data: formData,
      processData: false,
      contentType: false,
      headers: {
        Authorization: `Token ${this.getToken()}`,
      },
      success: (response) => {
        this.handle_update_event_response(response);
      },
      error: () => {
        this.show_update_event_error();
      },
    });
  },
};
